import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import authService from './api-authorization/AuthorizeService';
import Button from 'react-bootstrap/Button';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './ItemsList.css';
import utils from '../utils'
import { XCircle, Pencil, Download, LayoutTextSidebar, FiletypePdf, Trash } from 'react-bootstrap-icons';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import { exportToExcel } from '../utils/excelUtils';
import { exportToPdf } from '../utils/pdfUtils';
import Dropdown from "react-bootstrap/Dropdown";
import Form from "react-bootstrap/Form";

const ItemsList = forwardRef((props, ref) => {
    let itemUrl = `${props.itemUrlBase}/<itemId>`;
    if (props.itemUrlParams != null && props.itemUrlParams.trim().length > 0) {
        itemUrl = `${itemUrl}?${props.itemUrlParams}`;
    }
    const [isFirstFetch, setIsFirstFetch] = useState(true);

    const [loading, setLoading] = useState(false);

    const loaded = useRef(false);
    const [compObj, setCompObj] = useState({
        items: [],
        itemUrl: itemUrl,
        itemsForDisplay: [],
        sortColumn: props.uniqueIdColumn,
        sortDirection: 'asc',
        pages: 1,
        pageNumber: 1,
        pageSize: 50,
        selectValues: props.columns.filter(q => q.filter === 'select').reduce((a, q) => (a[q.id] = q.defaultFilter || '-1', a), {}),
        selectMultiValues: (props.allColumns || props.columns).filter(q => q.filter === 'selectMulti').reduce((a, q) => (a[q.id] = [], a), {}),
        selectMultiOptions: (props.allColumns || props.columns).filter(q => q.filter === 'selectMulti').reduce((a, q) => { a[q.id] = (q.defaultFilters || []).map(df => ({ id: df, label: df, isChecked: true })); return a; }, {}),
        selectMultiIds: (props.allColumns || props.columns).filter(q => q.filter === 'selectMulti').reduce((a, q) => (a[q.id] = q.selectId, a), {}),
        selectIds: props.columns.filter(q => q.filter === 'select').reduce((a, q) => (a[q.id] = q.selectId, a), {}),
        selectOptions: props.columns.filter(q => q.filter === 'select').reduce((a, q) => (a[q.id] = [], a), {}),
        searchValues: props.columns.filter(q => q.filter === 'search').reduce((a, q) => (a[q.id] = '', a), {}),
        dateRangeValues: props.columns.filter(q => q.filter === 'dateRange').reduce((a, q) => (a[q.id] = { from: '', to: '' }, a), {}),
        yearRangeValues: props.columns.filter(q => q.filter === 'yearRange').reduce((a, q) => (a[q.id] = { from: q.defaultFilter, to: q.defaultFilter }, a), {}),
        filteredItems: []
    });

    const [deleteItemId, setDeleteItemId] = useState(0);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowColumnDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


    useEffect(() => {
        const updateList = props.shouldUpdateList && props.shouldUpdateList();
        if (updateList || !loaded.current) {
            const initialize = async () => {
                await getItems();
                loaded.current = true;
            };
            initialize();
        }
    }, [props.shouldUpdateList]);

    const [showColumnDropdown, setShowColumnDropdown] = useState(false);
    const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

 
    const [selectedColumns, setSelectedColumns] = useState(
        props.columns.map(c => c.id)
    );

    useImperativeHandle(ref, () => ({
        getData: () => compObj,
    }));

    const sort = async (column) => {
        const tempObj = utils.getCopy(compObj);
        tempObj.sortColumn = column;
        tempObj.sortDirection = 'asc';
        const icon = document.getElementById(`column_${column}`);
        if (icon.classList.contains('bi-chevron-up')) {
            tempObj.sortDirection = 'desc';
        }
        await populateItemsForDisplay(tempObj);
    };

    const goToPage = async (pageNumber) => {
        const tempObj = utils.getCopy(compObj);
        tempObj.pageNumber = pageNumber;
        await populateItemsForDisplay(tempObj);
    };

    const updatePageSize = async (event) => {
        const tempObj = utils.getCopy(compObj);
        tempObj.pageSize = event.target.value;
        tempObj.pageNumber = 1;
        await populateItemsForDisplay(tempObj);
    };

    const search = async (event) => {
        if (event.key === 'Enter') {
            const tempObj = utils.getCopy(compObj);
            const key = event.target.id.substring(7);
            tempObj.searchValues[key] = event.target.value;
            await populateItemsForDisplay(tempObj);
        }
    }

    const clearSearch = async (event) => {
        if (event.target.value === '') {
            const tempObj = utils.getCopy(compObj);
            const key = event.target.id.substring(7);
            tempObj.searchValues[key] = '';
            await populateItemsForDisplay(tempObj);
        }
    }

    const updateSelect = async (event) => {
        const tempObj = utils.getCopy(compObj);
        const key = event.target.id.substring(7);
        tempObj.selectValues[key] = event.target.value;
        tempObj.pageNumber = 1;
        await populateItemsForDisplay(tempObj);
    };

    const updateSelectMulti = async (key, optionId, checked) => {
        const tempObj = utils.getCopy(compObj);

        tempObj.selectMultiOptions[key] = tempObj.selectMultiOptions[key].map(opt =>
            opt.id === optionId ? { ...opt, isChecked: checked } : opt
        );


        const selectedValues = tempObj.selectMultiOptions[key]
            .filter(o => o.isChecked)
            .map(o => o.id);

        tempObj.selectMultiValues[key] = selectedValues;
        tempObj.pageNumber = 1;
        setIsFirstFetch(false);
        await populateItemsForDisplay(tempObj);
    };


    const updateDateRange = async (event) => {
        const tempObj = utils.getCopy(compObj);
        if (event.target.id.startsWith('dateRange_from')) {
            // from range was updated
            const key = event.target.id.substring(15);
            tempObj.dateRangeValues[key]['from'] = event.target.value;
            const toDateElement = document.getElementById(`dateRange_to_${key}`);
            toDateElement.min = event.target.value;
        } else {
            // to range was updated
            const key = event.target.id.substring(13);
            tempObj.dateRangeValues[key]['to'] = event.target.value;
            const fromDateElement = document.getElementById(`dateRange_from_${key}`);
            fromDateElement.max = event.target.value;
        }
        tempObj.pageNumber = 1;
        await populateItemsForDisplay(tempObj);
    }

    const updateYearRange = async (event) => {
        const tempObj = utils.getCopy(compObj);
        const value = event.target.value === '-1' ? '' : event.target.value;
        if (event.target.id.startsWith('yearRange_from')) {
            // from range was updated
            const key = event.target.id.substring(15);
            tempObj.yearRangeValues[key]['from'] = value;
        } else {
            // to range was updated
            const key = event.target.id.substring(13);
            tempObj.yearRangeValues[key]['to'] = value;
        }
        tempObj.pageNumber = 1;
        await populateItemsForDisplay(tempObj);
    }

    const getSum = (values, labelFormat) => {
        let sum = 0;
        if (values.length > 0) {
            sum = values.reduce(function (a, b) {
                return a + b;
            });
        }
        return getLabel(sum, labelFormat);
    };

    const resetFilters = () => {
        const newCompObj = utils.getCopy(compObj);

        newCompObj.selectValues = props.columns
            .filter(q => q.filter === 'select')
            .reduce((a, q) => (a[q.id] = q.defaultFilter || '-1', a), {});

        newCompObj.selectIds = props.columns
            .filter(q => q.filter === 'select')
            .reduce((a, q) => (a[q.id] = q.selectId, a), {});

        newCompObj.selectOptions = props.columns
            .filter(q => q.filter === 'select')
            .reduce((a, q) => (a[q.id] = [], a), {});

        newCompObj.selectMultiValues = props.columns
            .filter(q => q.filter === 'selectMulti')
            .reduce((a, q) => (a[q.id] = [], a), {});

        newCompObj.selectMultiIds = props.columns
            .filter(q => q.filter === 'selectMulti')
            .reduce((a, q) => (a[q.id] = q.selectId, a), {});

        newCompObj.selectMultiOptions = (props.allColumns || props.columns)
            .filter(q => q.filter === 'selectMulti')
            .reduce((a, q) => { a[q.id] = (q.defaultFilters || []).map(df => ({ id: df, label: df, isChecked: true })); return a; }, {});

        newCompObj.searchValues = props.columns
            .filter(q => q.filter === 'search')
            .reduce((a, q) => (a[q.id] = '', a), {});

        newCompObj.dateRangeValues = props.columns
            .filter(q => q.filter === 'dateRange')
            .reduce((a, q) => (a[q.id] = { from: '', to: '' }, a), {});

        newCompObj.yearRangeValues = props.columns
            .filter(q => q.filter === 'yearRange')
            .reduce((a, q) => (a[q.id] = { from: q.defaultFilter, to: q.defaultFilter }, a), {});

        newCompObj.pageNumber = 1;

        setCompObj(newCompObj);
        populateItemsForDisplay(newCompObj);
    }

    useEffect(() => {
        if (!isFirstFetch) {
            resetFilters();
            setIsFirstFetch(false);
        }
    }, [props.columns, props.selectedColumns]);

    const getLabel = (value, labelFormat) => {
        if (labelFormat === 'dollar') {
            return utils.getDollarString(value);
        } else if (labelFormat === 'date') {
            if (value instanceof Date) {
                return value.toLocaleDateString();
            } else {
                return (new Date(value)).toLocaleDateString();
            }
        }
        return value;
    };

    let minPage = compObj.pageNumber === 1 ? 1 : compObj.pageNumber - 1;
    const maxPage = compObj.pages < minPage + 2 ? compObj.pages : minPage + 2;
    if (maxPage === compObj.pageNumber && maxPage > 2) {
        minPage = maxPage - 2;
    }
    let pageNumbers = [];
    for (let i = minPage - 1; i < maxPage; i++) {
        pageNumbers.push(i + 1);
    }

    const getItems = async () => {
        const tempObj = utils.getCopy(compObj);
        tempObj.items = [];
        tempObj.loading = true;
        setLoading(true);
        const token = await authService.getAccessToken();
        const response = await fetch(props.getItemsUrl, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        });
        const json = await response.json();
        tempObj.items = json.map(props.mapFunction);
        tempObj.pages = Math.ceil(tempObj.items.length / tempObj.pageSize);
        tempObj.loading = false;
        await populateItemsForDisplay(tempObj);
        setLoading(false);
    }

    const populateItemsForDisplay = async (tempObj) => {
        let filteredItems = [...tempObj.items];
        for (const key in tempObj.searchValues) {
            filteredItems = filteredItems.filter(q => tempObj.searchValues[key] === '' || q[key]?.toString().toUpperCase().includes(tempObj.searchValues[key].toUpperCase()));
        }

        // Filter select values
        for (const key in tempObj.selectValues) {
            const filterValue = tempObj.selectValues[key];
            if (filterValue === '-1') continue;

            const propName = tempObj.selectIds[key] ?? key;

            const preFilteredItems = filteredItems.filter(item => {
                const cell = String(item[propName]).toLowerCase();
                return String(cell ?? '') === filterValue.toLowerCase();
            });
            if (preFilteredItems.length > 0) {
                filteredItems = preFilteredItems;
            }
        }


        // Get distinct options for multi-selects
        for (const key in tempObj.selectMultiOptions) {
            const availableOptions = getDistinctItems(
                filteredItems.map(q => ({
                    id: q[tempObj.selectMultiIds[key]],
                    label: q[key]
                }))
            );

            const existing = tempObj.selectMultiOptions[key] || [];

            const allExist =
                existing.length > 0 &&
                availableOptions.every(opt =>
                    existing.some(e => e.id === opt.id)
                );

            if (allExist) {
                tempObj.selectMultiOptions[key] = existing.map(opt => {
                    const match = availableOptions.find(a => a.id === opt.id);
                    return match ? { ...match, isChecked: opt.isChecked } : opt;
                });
            } else {
                const merged = availableOptions.map(opt => {
                    const found = existing.find(e => e.id === opt.id);
                    return { ...opt, isChecked: found ? found.isChecked : false };
                });
                tempObj.selectMultiOptions[key] = merged;
            }
        }

        // Filter select multi values
        for (const key in tempObj.selectMultiValues) {
            const filterValues = tempObj.selectMultiValues[key];
            const propName = tempObj.selectMultiIds[key] ?? key;

            const preFilteredItems = filteredItems.filter(item => {
                const cellValue = String(item[propName] ?? "").toLowerCase();
                return filterValues?.some(fv => String(fv).toLowerCase() === cellValue);
            });

            if (!filterValues || filterValues?.length === 0) {
                if (tempObj.selectMultiOptions[key] && tempObj.selectMultiOptions[key].length > 0) {
                    const allUnchecked = tempObj.selectMultiOptions[key].every(o => !o.isChecked);
                    if (allUnchecked) {
                        continue;
                    }

                    filteredItems = filteredItems.filter(item => {
                        const cellValue = String(item[propName] ?? "").toLowerCase();
                        return tempObj.selectMultiOptions[key]
                            .filter(o => o.isChecked)
                            .some(o => String(o.id).toLowerCase() === cellValue);
                    });
                }
                continue;
            }

            if (preFilteredItems.length > 0) {
                filteredItems = preFilteredItems;
            }
        }

        for (const key in tempObj.dateRangeValues) {
            filteredItems = filteredItems.filter(q => tempObj.dateRangeValues[key]['from'] === '' || q[key].getTime() >= new Date(`${tempObj.dateRangeValues[key]['from']}T00:00:00`).getTime());
            filteredItems = filteredItems.filter(q => tempObj.dateRangeValues[key]['to'] === '' || q[key].getTime() <= new Date(`${tempObj.dateRangeValues[key]['to']}T00:00:00`).getTime());
        }
        for (const key in tempObj.yearRangeValues) {
            filteredItems = filteredItems.filter(q => tempObj.yearRangeValues[key]['from'] === '' || q[key] >= tempObj.yearRangeValues[key]['from']);
            filteredItems = filteredItems.filter(q => tempObj.yearRangeValues[key]['to'] === '' || q[key] <= tempObj.yearRangeValues[key]['to']);
        }

        tempObj.itemsForDisplay = filteredItems.sort((a, b) => {
            let aComp = a[tempObj.sortColumn], bComp = b[tempObj.sortColumn];
            const isANumber = !isNaN(parseFloat(aComp)) && isFinite(aComp);
            const isBNumber = !isNaN(parseFloat(bComp)) && isFinite(bComp);

            if (isANumber && isBNumber) {
                aComp = parseFloat(aComp);
                bComp = parseFloat(bComp);
            } else if (aComp instanceof Date && bComp instanceof Date) {
                aComp = aComp.getTime();
                bComp = bComp.getTime();
            } else {
                aComp = (aComp ?? '').toString().toUpperCase();
                bComp = (bComp ?? '').toString().toUpperCase();
            }

            if (aComp instanceof Date) {
                aComp = aComp.getTime();
                bComp = bComp.getTime();
            }
            if (aComp < bComp) {
                return tempObj.sortDirection === 'asc' ? -1 : 1;
            }
            if (aComp > bComp) {
                return tempObj.sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        }).slice(tempObj.pageSize * (tempObj.pageNumber - 1), tempObj.pageSize * tempObj.pageNumber);

        for (const key in tempObj.selectOptions) {
            tempObj.selectOptions[key] = getDistinctItems(filteredItems.map((q) => { return { id: q[tempObj.selectIds[key]], label: q[key] } }));
        }




        tempObj.pages = Math.ceil(filteredItems.length / tempObj.pageSize);

        tempObj.filteredItems = [...filteredItems];

        setCompObj(tempObj);
    }

    const getDistinctItems = (items) => {
        const ids = [];
        return items.filter((q) => {
            if (ids.indexOf(q.id) >= 0) return false;
            ids.push(q.id);
            return true;
        }).sort((a, b) => {
            if (a.label?.toString().toUpperCase() < b.label?.toString().toUpperCase()) return -1;
            if (a.label?.toString().toUpperCase() > b.label?.toString().toUpperCase()) return 1;
            return 0;
        });
    }

    const getYears = (key) => {
        const currentYear = new Date().getFullYear();
        let startYear = currentYear;
        const dataYears = compObj.items.map(q => q[key]);
        if (dataYears.length > 0) {
            const minYear = Math.min(...dataYears);
            if (minYear < currentYear) {
                startYear = minYear;
            }
        }
        const years = [];
        for (let y = startYear; y <= currentYear + 30; y++) {
            years.push(y);
        }
        return years;
    }

    const downloadExcel = async () => {
        const dataToExport = props.filteredItems || compObj.filteredItems;

        const filteredColumns = props.columns.filter(
            col => !['editItem', 'deleteItem', 'clickToItem', 'clickToObject'].includes(col.labelFormat)
        );

        const data = dataToExport.map(item => {
            const row = {};
            filteredColumns.forEach(col => {
                let value = item[col.id];

                if (col.labelFormat === 'dollar') {
                    value = typeof value === 'number' ? value : 0;
                } else if (col.labelFormat === 'date') {
                    value = value ? new Date(value).toLocaleDateString() : '';
                } else if (col.labelFormat === 'percentagePill') {
                    value = `${value ?? 0}%`;
                }

                row[col.id] = value ?? '';
            });
            return row;
        });

        const fileName = props.itemName
            ? `${props.itemName}_Export_${new Date().toISOString().split("T")[0]}`
            : "Export";

        const columnFormats = props.columnFormats || {};
        const hasFormats = Object.keys(columnFormats).length > 0;

        if (hasFormats) {
            await exportToExcel(data, fileName, columnFormats);
        } else {
            const headers = filteredColumns.map(c => c.name || c.id);
            const keys = filteredColumns.map(c => c.id);

            const csvRows = [headers.join(",")];
            for (const item of data) {
                const row = keys.map(k => {
                    let val = item[k];
                    if (val === null || val === undefined) return "";
                    val = val.toString().replace(/"/g, '""');
                    if (val.includes(",") || val.includes("\n")) {
                        val = `"${val}"`;
                    }
                    return val;
                });
                csvRows.push(row.join(","));
            }

            const csvBlob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(csvBlob);
            link.download = `${fileName}.csv`;
            link.click();
        }
    };

    const downloadPdf = async () => {
        const dataToExport = props.filteredItems || compObj.filteredItems;

        // Filter out non-exportable columns
        const filteredColumns = props.columns.filter(
            col => !['editItem', 'deleteItem', 'clickToItem', 'clickToObject'].includes(col.labelFormat)
        );

        const tableTitle = props.itemName ? `${props.itemName} Export` : 'Table Export';
        const fileName = props.itemName
            ? `${props.itemName}_Export_${new Date().toISOString().split("T")[0]}`
            : "Export";

        exportToPdf(dataToExport, filteredColumns, fileName, tableTitle);
    };

    if (loading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '250px' }}>
                        <div className="text-center">
                            Loading...
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const excludedDuplicates = ['linkToItem', 'clickToObject', 'percentagePill', 'clickToItem', 'linkToProject', 'deleteItem'];

    const hasRowSelection = !!props.setSelectedRows && !!props.rowId;
    const selectedRows = props.selectedRows || [];

    const isRowSelected = (row) => {
        if (!hasRowSelection) return false;
        return selectedRows.includes(row[props.rowId]);
    };

    const areAllVisibleSelected =
        hasRowSelection &&
        compObj.itemsForDisplay.length > 0 &&
        compObj.itemsForDisplay.every(item => selectedRows.includes(item[props.rowId]));

    const handleToggleSelectAllVisible = () => {
        if (!hasRowSelection) return;

        if (areAllVisibleSelected) {
            const visibleIds = new Set(compObj.itemsForDisplay.map(item => item[props.rowId]));
            const newSelected = selectedRows.filter(id => !visibleIds.has(id));
            props.setSelectedRows(newSelected);
        } else {
            const visibleIds = compObj.itemsForDisplay.map(item => item[props.rowId]);
            const merged = Array.from(new Set([...selectedRows, ...visibleIds]));
            props.setSelectedRows(merged);
        }
    };

    const handleRowCheckboxChange = (row) => {
        if (!hasRowSelection) return;

        const id = row[props.rowId];
        if (selectedRows.includes(id)) {
            props.setSelectedRows(selectedRows.filter(x => x !== id));
        } else {
            props.setSelectedRows([...selectedRows, id]);
        }
    };

    return (
        <div className="card">
            {props.showHeader &&
                <div className="card-header d-md-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#F8F9FA' }}>
                    <div className="d-md-flex align-items-center gap-2">
                        {
                            (props.changeView !== null && props.changeView !== undefined) &&
                            <Button type="button" variant="secondary" onClick={props.changeView}>Change View</Button>
                        }
                        {
                            (props.addItemMethod === undefined || props.addItemMethod === null) ?
                            <a href={props.addItemUrl} className="btn btn-primary">Add {props.itemName}</a>
                            :
                            <Button type="button" variant="primary" onClick={props.addItemMethod}>Add {props.itemName}</Button>
                        }
                        {hasRowSelection && props.bulkRemoveFunction && (
                            <Button
                                type="button"
                                variant={selectedRows.length === 0 ? "outline-secondary" : "outline-danger"}
                                disabled={selectedRows.length === 0}
                                onClick={() => setShowBulkDeleteModal(true)}
                            >
                                <Trash
                                    size={20} 
                                />
                            </Button>
                        )}

                        {
                            props.showDownload &&
                            <Button
                                type="button"
                                variant="outline-primary"
                                onClick={props.onDownload || downloadExcel}
                                title="Download Excel"
                            >
                                <Download />
                            </Button>
                        }
                        {
                            props.showDownload &&
                            <Button
                                type="button"
                                variant="outline-danger"
                                onClick={downloadPdf}
                                title="Export to PDF"
                            >
                                <FiletypePdf />
                            </Button>
                        }
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        {props.allColumns && props.allColumns.length > 0 && (
                        <div className="position-relative" ref={dropdownRef}>
                            <Button
                                type="button"
                                variant="outline-secondary"
                                onClick={() => setShowColumnDropdown(!showColumnDropdown)}
                            >
                                <span className="d-flex align-items-center gap-2">
                                    <LayoutTextSidebar />
                                    <i className={`bi ${showColumnDropdown ? "bi-caret-up-fill" : "bi-caret-down-fill"}`} />
                                </span>
                            </Button>

                            {showColumnDropdown && (
                                <div
                                    className="border rounded bg-white p-2 shadow position-absolute"
                                    style={{ zIndex: 1000, minWidth: "200px", right: 0 }}
                                >
                                    {props.allColumns.map(col => (
                                        <div key={col.id} className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`col_${col.id}`}
                                                checked={selectedColumns.includes(col.id)}
                                                onChange={(e) => {
                                                    let updatedCols;
                                                    if (e.target.checked) {
                                                        const newSelected = [...selectedColumns, col.id];
                                                        setSelectedColumns(newSelected);
                                                        updatedCols = props.allColumns.filter(c => newSelected.includes(c.id));
                                                    } else {
                                                        const newSelected = selectedColumns.filter(c => c !== col.id);
                                                        setSelectedColumns(newSelected);
                                                        updatedCols = props.allColumns.filter(c => newSelected.includes(c.id));
                                                    }
                                                    props.setColumns(updatedCols);
                                                }}
                                            />

                                            <label className="form-check-label" htmlFor={`col_${col.id}`}>
                                                {col.name}
                                            </label>
                                        </div>
                                    ))}

                                    <div className="d-flex justify-content-end gap-2 mt-2">
                                        <Button size="sm" variant="secondary" onClick={() => setShowColumnDropdown(false)}>
                                            Done
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    </div>  
                </div>
            }
            {/* Table handling error messages */}
            {

                    <><div className="card-body">
                        <table className="table table-striped" aria-labelledby="tableLabel">
                            <thead>
                                {/* 1) Parent‐group headers */}
                                <tr key="groupHeaders">
                                    {hasRowSelection && (
                                        <th
                                            rowSpan={3}
                                            className="text-center"
                                            style={{ width: '40px' }}
                                        >
                                            <Form.Check
                                                type="checkbox"
                                                checked={areAllVisibleSelected}
                                                onChange={handleToggleSelectAllVisible}
                                            />
                                        </th>
                                    )}

                                    {props.columns.map(c => {
                                        if (c.parent) {
                                            const siblings = props.columns.filter(x => x.parent === c.parent);

                                            if (siblings[0].id === c.id) {
                                                return (
                                                    <th
                                                        key={c.parent}
                                                        colSpan={siblings.length}
                                                        className="sortColumnHeader text-center"
                                                    >
                                                        {c.parent}
                                                    </th>
                                                );
                                            }
                                            return null;
                                        } else {
                                            return (
                                                <th
                                                    key={c.id}
                                                    rowSpan={2}
                                                    className="sortColumnHeader"
                                                    onClick={() => c.sortable && sort(c.id)}
                                                >
                                                    {c.name}
                                                    <i
                                                        id={`column_${c.id}`}
                                                        className={
                                                            compObj.sortColumn === c.id
                                                                ? (compObj.sortDirection === 'asc'
                                                                    ? 'bi bi-chevron-up'
                                                                    : 'bi bi-chevron-down')
                                                                : ''
                                                        }
                                                    />
                                                </th>
                                            );
                                        }
                                    })}
                                </tr>

                                {/* 2) Child column headers */}
                                <tr key="columnHeaders">
                                    {props.columns
                                        .filter(c => c.parent)
                                        .map(c => (
                                            <th
                                                key={`column_${c.id}`}
                                                className="sortColumnHeader"
                                                onClick={() => c.sortable && sort(c.id)}
                                            >
                                                {c.name}
                                                <i
                                                    id={`column_${c.id}`}
                                                    className={
                                                        compObj.sortColumn === c.id
                                                            ? (compObj.sortDirection === 'asc'
                                                                ? 'bi bi-chevron-up'
                                                                : 'bi bi-chevron-down')
                                                            : ''
                                                    }
                                                />
                                            </th>
                                        ))}
                                </tr>

                                {/* 3) Filters row */}
                                <tr key="columnFilters">
                                    {props.columns.map(c => (
                                        <th key={`select_${c.id}`}>
                                            {c.filter === 'search' && (
                                                <input
                                                    id={`search_${c.id}`}
                                                    type="search"
                                                    className="form-control"
                                                    placeholder="Search"
                                                    onKeyDown={search}
                                                    onChange={clearSearch}
                                                    defaultValue={compObj.searchValues[c.id]}
                                                />
                                            )}
                                            {c.filter === 'select' && (
                                                <select
                                                    id={`select_${c.id}`}
                                                    className="form-select"
                                                    value={compObj.selectValues[c.id]}
                                                    onChange={updateSelect}
                                                >
                                                    <option value="-1">All</option>
                                                    {(compObj.selectOptions[c.id] || []).map(o => (
                                                        <option key={o.id} value={o.id}>{o.label}</option>
                                                    ))}
                                                </select>
                                            )}
                                            {c.filter === 'selectMulti' && (
                                                <Dropdown className="w-100">
                                                    <Dropdown.Toggle
                                                        variant="outline-secondary"
                                                        className="w-100 text-start d-flex justify-content-between align-items-center"
                                                        id={`dropdown-${c.id}`}
                                                    >
                                                        {((compObj.selectMultiValues[c.id] || []).length > 0 || (compObj.selectMultiOptions[c.id].filter(a => a.isChecked) && isFirstFetch))
                                                            ? `${(compObj.selectMultiValues[c.id].length > 0 ? compObj.selectMultiValues[c.id] : compObj.selectMultiOptions[c.id].filter(a => a.isChecked)).length} selected`
                                                            : "All"}
                                                    </Dropdown.Toggle>

                                                    <Dropdown.Menu
                                                        className="p-2 shadow w-100"
                                                        style={{ maxHeight: "220px", overflowY: "auto" }}
                                                    >
                                                        {(compObj.selectMultiOptions[c.id] || []).length === 0 ? (
                                                            <div className="px-2 text-muted small">(No options available)</div>
                                                        ) : (
                                                            compObj.selectMultiOptions[c.id].map(o => (
                                                                <Form.Check
                                                                    key={o.id}
                                                                    type="checkbox"
                                                                    id={`selectMulti_${c.id}_${o.id}`}
                                                                    label={o.label}
                                                                    checked={o.isChecked}
                                                                    onChange={(e) => updateSelectMulti(c.id, o.id, e.target.checked)}
                                                                />
                                                            ))
                                                        )}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            )}

                                            {c.filter === 'dateRange' && (
                                                <div className="container-sm">
                                                    <div className="row align-items-start">
                                                        <div className="col-5">
                                                            <input
                                                                id={`dateRange_from_${c.id}`}
                                                                type="date"
                                                                className="form-control"
                                                                onChange={updateDateRange}
                                                            />
                                                        </div>
                                                        <div className="col-5">
                                                            <input
                                                                id={`dateRange_to_${c.id}`}
                                                                type="date"
                                                                className="form-control"
                                                                onChange={updateDateRange}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {c.filter === 'yearRange' && (
                                                <div className="container-sm">
                                                    <div className="row align-items-start">
                                                        <div className="col-5">
                                                            <select
                                                                id={`yearRange_from_${c.id}`}
                                                                className="form-select"
                                                                onChange={updateYearRange}
                                                                value={compObj.yearRangeValues[c.id]?.from}
                                                            >
                                                                <option value="">From</option>
                                                                <option value="-1">All</option>
                                                                {getYears(c.id).map(year => (
                                                                    <option
                                                                        key={`from_${year}`}
                                                                        value={year}
                                                                        disabled={
                                                                            compObj.yearRangeValues[c.id]?.to !== '' &&
                                                                            year > compObj.yearRangeValues[c.id]?.to
                                                                        }
                                                                    >
                                                                        {year}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="col-5">
                                                            <select
                                                                id={`yearRange_to_${c.id}`}
                                                                className="form-select"
                                                                onChange={updateYearRange}
                                                                value={compObj.yearRangeValues[c.id]?.to}
                                                            >
                                                                <option value="">To</option>
                                                                <option value="-1">All</option>
                                                                {getYears(c.id).map(year => (
                                                                    <option
                                                                        key={`to_${year}`}
                                                                        value={year}
                                                                        disabled={
                                                                            compObj.yearRangeValues[c.id]?.from !== '' &&
                                                                            year < compObj.yearRangeValues[c.id]?.from
                                                                        }
                                                                    >
                                                                        {year}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            </thead>


                            <tbody>
                                {
                                    compObj.itemsForDisplay.length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan={props.columns.length + (hasRowSelection ? 1 : 0)}
                                                className="p-0"
                                            >
                                                <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '250px' }}>
                                                    <div className="text-center">
                                                        <div className="alert alert-light mb-0 py-4" role="alert" >
                                                            <i className="bi bi-info-circle fs-4 mb-2 text-secondary" />
                                                            <div className="fw-bold">{props.noDataMessage || `No ${props.itemName?.toLowerCase()}s found.`}</div>
                                                            <div className="text-muted small">
                                                                If you believe you should have access to {props.itemName?.toLowerCase()}s, please contact your administrator.
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        compObj.itemsForDisplay.map(item =>
                                            <tr key={item[props.uniqueIdColumn]}>
                                                {hasRowSelection && (
                                                    <td className="text-center" style={{ width: '40px' }}>
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={isRowSelected(item)}
                                                            onChange={() => handleRowCheckboxChange(item)}
                                                        />
                                                    </td>
                                                )}

                                                {props.columns.map(column =>
                                                    <td key={column.id} className={column.labelFormat === 'small' ? 'small-text' : ''}>
                                                        {column.labelFormat === 'linkToItem' && (
                                                            props.onItemClick ? (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-link p-0"
                                                                    onClick={() => props.onItemClick(item.id)}
                                                                >
                                                                    {item[column.id]}
                                                                </button>
                                                            ) : (
                                                                <a
                                                                    href={
                                                                        (() => {
                                                                            const baseUrl = column.itemLink
                                                                                ? `/${column.itemLink}/${item[column.itemLinkId]}`
                                                                                : itemUrl.replace('<itemId>', item.id);

                                                                            const rawParams = Array.isArray(column.itemLinkParams)
                                                                                ? Object.assign({}, ...column.itemLinkParams)
                                                                                : (column.itemLinkParams ?? {});

                                                                            const queryParams = Object.keys(rawParams).length
                                                                                ? `${new URLSearchParams(rawParams).toString()}`
                                                                                : '';

                                                                            return queryParams ? `${baseUrl}?${queryParams}` : baseUrl;
                                                                        })()
                                                                    }
                                                                >
                                                                    {item[column.id]}
                                                                </a>
                                                            )
                                                        )}

                                                        {column.labelFormat === 'clickToItem' && <a className="link" style={{ cursor: "pointer" }} onClick={(e) => props.editItemMethod(`${item.id}`, e)}>{item[column.id]}</a>}
                                                        {column.labelFormat === 'clickToObject' && <a className="link" style={{ cursor: "pointer" }} onClick={() => props.editItemMethod(item)}>{item[column.id]}</a>}
                                                        {column.labelFormat === 'linkToProject' && <a href={`projects/${item['projectId']}`}>{item['project']}</a>}

                                                        {column.labelFormat === 'percentagePill' &&
                                                            <span
                                                                style={{ width: "60px" }}
                                                                className={`badge rounded-pill px-3 ${item[column.id] < (props.threshold !== null ? props.threshold : 50) ? 'bg-success' : item[column.id] < 100 ? 'bg-warning' : 'bg-danger'}`}
                                                            >
                                                                {`${item[column.id]}%`}
                                                            </span>
                                                        }
                                                        {column.labelFormat === 'editItem' && (
                                                            <div style={{ textAlign: 'right' }}>
                                                                <Button
                                                                    variant="link"
                                                                    onClick={() => props.editItemMethod(item.id)}
                                                                >
                                                                    <Pencil color="black" />
                                                                </Button>
                                                            </div>
                                                        )}

                                                        {column.labelFormat === 'deleteItem' && (
                                                            <Button
                                                                variant="link"
                                                                onClick={async () => {
                                                                    if (props.preDeleteCheck === undefined || await props.preDeleteCheck(item.id)) {
                                                                        setDeleteItemId(item.id);
                                                                        setShowDeleteModal(true);
                                                                    }
                                                                }}
                                                            >
                                                                <XCircle color="red" />
                                                            </Button>
                                                        )}

                                                        {!excludedDuplicates.includes(column.labelFormat) && getLabel(item[column.id], column.labelFormat)}
                                                    </td>
                                                )}
                                            </tr>
                                        )
                                    )
                                }
                            </tbody>

                        </table>
                    </div>
                        <div className="card-footer">
                            <div className="row justify-content-center justify-content-sm-between align-items-sm-center">
                                <div className="col-sm mb-2 mb-sm-0">
                                    <div className="d-flex justify-content-center justify-content-sm-start align-items-center">
                                        <select className="js-select form-select form-select-borderless w-auto" aria-label="" onChange={updatePageSize} value={compObj.pageSize}>
                                            <option value="10" key="ps_10">10</option>
                                            <option value="20" key="ps_20">20</option>
                                            <option value="30" key="ps_30">30</option>
                                            <option value="50" key="ps_50">50</option>
                                            <option value="100" key="ps_100">100</option>
                                            <option value="250" key="ps_250">250</option>
                                        </select>
                                        <span>&nbsp;Items per page</span>
                                    </div>
                                </div>
                                <div className="col-sm-auto">
                                    <nav aria-label="Page navigation example">
                                        <ul className="pagination">
                                            <li key="prevPage" className={`page-item ${compObj.pageNumber === 1 ? 'disabled' : ''}`} >
                                                <a className="page-link" aria-label="Previous" onClick={() => { goToPage(compObj.pageNumber - 1) }}>
                                                    <span aria-hidden="true">&laquo;</span>
                                                </a>
                                            </li>
                                            {pageNumbers.map(pageNumber =>
                                                <li key={pageNumber} className={`page-item ${pageNumber === compObj.pageNumber ? 'active' : ''}`}>
                                                    <a className="page-link" onClick={() => { goToPage(pageNumber) }}>{pageNumber}</a>
                                                </li>
                                            )}
                                            <li key="nextPage" className={`page-item ${compObj.pageNumber === compObj.pages ? 'disabled' : ''}`}>
                                                <a className="page-link" aria-label="Next" onClick={() => { goToPage(compObj.pageNumber + 1) }}>
                                                    <span aria-hidden="true">&raquo;</span>
                                                </a>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            </div>
                        </div></>
            }

            <Modal size="sm" show={showDeleteModal} onHide={() => { setShowDeleteModal(false) }} backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Delete {props.itemName}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{props.deleteItemMessage !== undefined && props.deleteItemMessage.length > 0 ? props.deleteItemMessage : `Are you sure you want to remove this ${props.itemName}?`}</Modal.Body>
                <Modal.Footer>
                    <Container>
                        <Row>
                            <Col className="col-6">
                                <Button onClick={async () => {
                                    await props.deleteItemMethod(deleteItemId);
                                    await setShowDeleteModal(false);
                                }}>Yes</Button>
                            </Col>
                            <Col className="col-6 text-end">
                                <Button variant="secondary" onClick={() => { setShowDeleteModal(false) }}>No</Button>
                            </Col>
                        </Row>
                    </Container>
                </Modal.Footer>
            </Modal>

            <Modal
                size="sm"
                show={showBulkDeleteModal}
                onHide={() => setShowBulkDeleteModal(false)}
                backdrop="static"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Delete {props.itemName}{selectedRows.length > 1 ? "s" : ""}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    Are you sure you want to delete {selectedRows.length > 1 ? `these ${selectedRows.length}` : `this`} {props.itemName}{selectedRows.length > 1 ? "s" : ""}?
                </Modal.Body>

                <Modal.Footer>
                    <Container>
                        <Row>
                            <Col className="col-6">
                                <Button
                                    variant="danger"
                                    onClick={async () => {
                                        await props.bulkRemoveFunction(selectedRows);
                                        setShowBulkDeleteModal(false);
                                    }}
                                >
                                    Yes
                                </Button>
                            </Col>

                            <Col className="col-6 text-end">
                                <Button
                                    variant="secondary"
                                    onClick={() => setShowBulkDeleteModal(false)}
                                >
                                    No
                                </Button>
                            </Col>
                        </Row>
                    </Container>
                </Modal.Footer>
            </Modal>


        </div>
    );
});

export default ItemsList;